import clsx from 'clsx';
import { FunctionComponent, useState } from 'react';
import { API } from '../api/rest';
import { Form, FormBody, Input, Label } from './Base';
import { useTurnstile } from './Turnstile';
import Button from './atoms/button';

type InputCfg = {
  label: string;
  description?: string;
  id: string;
  type: JSX.IntrinsicElements['input']['type'] | 'longtext';
  required?: boolean;
};

const AUTO_SCROLL_CLASS = 'auto-scroll-to';
const HEADER_HEIGHT = 80;

const FIELDS: InputCfg[] = [
  // DUPED in pricing.handlers.js
  { label: 'Name', id: 'fldsArLjkPzpuUT5f', type: 'text', required: true },
  {
    label: 'Email Address',
    id: 'fldquuDmWfgN0SyCT',
    type: 'email',
    required: true,
  },
  { label: 'Company', id: 'fldgNJ9Kv55fSylm8', type: 'text', required: true },
  {
    label: 'Number of seats requested',
    id: 'fldxRlgnL1MxWG7h4',
    type: 'number',
    required: true,
  },
  {
    label: 'Notes',
    description:
      'Please share any information about existing GoFullPage Premium accounts in your organization, or anything else you’d like us to know.',
    id: 'fldzCEqahOxHHQUMR',
    type: 'longtext',
  },
];

const AirtableForm = () => {
  const [formData, setFormData] = useState<Record<string, undefined | string>>(
    {},
  );
  const [msg, setMsg] = useState<{
    text: string;
    type: 'success' | 'error';
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const captcha = useTurnstile();
  const hideForm = msg?.type === 'success';

  // useEffect(() => {
  //   setMsg({
  //     type: 'success',
  //     text: 'Hey this is a message, what do you think about this?',
  //   });
  //   window.setTimeout(() => _scrollToError(), 50);
  // }, []);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    if (!captcha.token) {
      setMsg({
        type: 'error',
        text: 'Please complete the captcha before submitting.',
      });
      window.setTimeout(() => _scrollToError(), 50);
      return;
    }
    setLoading(true);

    API.post({
      path: '/pricing/enterprise-request',
      body: { ...formData, captcha_token: captcha.token },
    })
      .then((resp) => {
        if (resp.type === 'success') {
          setMsg({
            type: 'success',
            text: 'Thank you for getting in touch! We will reach out to schedule a meeting.',
          });
        } else {
          setMsg({
            type: 'error',
            text:
              resp.message ||
              `Unknown error, please contact support@gofullpage.com`,
          });
        }
      })
      .catch((error) => {
        setMsg({
          type: 'error',
          text: `Unknown error (${error?.message || 'cannot access message'}), please contact support@gofullpage.com`,
        });
      })
      .finally(() => {
        setLoading(false);
        captcha.reset();
        // HACK - scroll to error
        window.setTimeout(() => _scrollToError(), 50);
      });
  };

  return (
    <div className="rounded-md border border-solid border-gray-200 bg-white p-8 text-left">
      <h1 className="!m-0 !mb-2 !p-0">
        Get in touch with the GoFullPage Sales Team
      </h1>
      <p className="!m-0 !mb-6 !p-0">
        Discover how GoFullPage Enterprise can empower your team with premium
        tools for seamless, full-page screen captures. Fill out the form below
        to request a demo, or learn more about our offerings for teams and
        entire firms.
      </p>
      <Form onSubmit={onSubmit}>
        <FormBody className="space-y-6">
          {msg && (
            <div
              className={clsx(
                AUTO_SCROLL_CLASS,
                'rounded-md p-4',
                msg.type === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-green-50 text-green-800',
              )}
            >
              {msg.text}
            </div>
          )}
          {!hideForm &&
            FIELDS.map(({ label, description, id, type, required }) => {
              const value = formData[id] ?? '';
              const onChange = (
                e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
              ) => {
                const value = e.target.value;
                setFormData((prevState) => ({
                  ...prevState,
                  [id]: value,
                }));
              };

              return (
                <div key={id} className="space-y-2">
                  <Label className="block font-medium" key={id} htmlFor={id}>
                    {label}
                    {required && <Required />}
                  </Label>
                  {Boolean(description) && (
                    <p className="!mt-0 text-sm text-gray-500">{description}</p>
                  )}
                  {type === 'longtext' ? (
                    <textarea
                      className="w-full font-sans"
                      name={id}
                      value={value}
                      onChange={onChange}
                      required={required}
                      // TODO - auto-height
                      rows={3}
                    />
                  ) : (
                    <Input
                      className="w-full"
                      name={id}
                      type={type}
                      value={value}
                      onChange={onChange}
                      required={required}
                    />
                  )}
                </div>
              );
            })}
        </FormBody>
        {!hideForm && (
          <>
            <div className="mb-4">{captcha.widget}</div>
            <Button loading={loading} type="submit" disabled={loading}>
              Submit
            </Button>
          </>
        )}
      </Form>
    </div>
  );
};

// import { loadScript } from '../utils/load-script';

// const SCRIPT_URL = 'https://static.airtable.com/js/embed/embed_snippet_v1.js';
// const SCRIPT_ID = '_airtable_embed';

// const AirtableForm = () => {
//   useEffect(() => {
//     loadScript(SCRIPT_ID, SCRIPT_URL);
//   }, []);

//   return (
//     <iframe
//       className="airtable-embed airtable-dynamic-height"
//       src="https://airtable.com/embed/app0kqkfRWnxN4HCj/shr6jhBksXh4RuDhg?backgroundColor=red"
//       frameBorder={0}
//       width="100%"
//       height="973"
//       style={{ background: 'transparent', border: '1px solid #ccc' }}
//     ></iframe>
//   );
// };

AirtableForm.displayName = 'AirtableForm';

export default AirtableForm;

// ## Helpers

const Required: FunctionComponent<Record<string, never>> = () => {
  return <span className="ml-0.5 text-red-600">*</span>;
};

const _scrollToError = () => {
  const elt = document.querySelector(`.${AUTO_SCROLL_CLASS}`);
  if (elt) {
    const y =
      elt.getBoundingClientRect().top + window.pageYOffset - HEADER_HEIGHT;

    window.scrollTo({
      top: y,
      behavior: 'smooth',
    });
  }
};
